import type { KenoBetType, KenoDraw } from '@nexbit/types'

/**
 * The board's view of a draw, in the reference game's own vocabulary.
 *
 * The page reuses the reference markup and dictionary, which name the nineteen bets o1–o21
 * (o5/o6 unused) and the roadmap outcomes by numeric codes. This file is the one place that
 * translates between those and our KenoBetType, so components never spell either by hand.
 */

export type OddCode =
  | 'o1' | 'o2' | 'o3' | 'o4' | 'o7' | 'o8' | 'o9' | 'o10' | 'o11' | 'o12'
  | 'o13' | 'o14' | 'o15' | 'o16' | 'o17' | 'o18' | 'o19' | 'o20' | 'o21'

export const ODD_OF: Record<KenoBetType, OddCode> = {
  big: 'o1', small: 'o2', odd: 'o3', even: 'o4',
  dragon: 'o7', dt_tie: 'o8', tiger: 'o9',
  up: 'o10', ud_tie: 'o11', down: 'o12',
  big_odd: 'o13', big_even: 'o14', small_odd: 'o15', small_even: 'o16',
  gold: 'o17', wood: 'o18', water: 'o19', fire: 'o20', earth: 'o21',
}

export const BET_OF = Object.fromEntries(
  Object.entries(ODD_OF).map(([bet, odd]) => [odd, bet]),
) as Record<OddCode, KenoBetType>

/** What the card's summary strip and the winning-cell highlight show for a set of balls. */
export interface BoardSummary {
  sum: number | null
  /** Tens and units digit of the sum — the มังกร / เสือ boxes. */
  dcnt: number | null
  tcnt: number | null
  /** Balls in 1–40 (บน) and 41–80 (ล่าง) — counted over the balls shown so far. */
  hcnt: number
  lcnt: number
  winners: Partial<Record<OddCode, boolean>>
}

export const EMPTY_SUMMARY: BoardSummary = { sum: null, dcnt: null, tcnt: null, hcnt: 0, lcnt: 0, winners: {} }

/**
 * Port of the reference setWinner(): recomputed on every ball while they flip in, so the
 * counts climb as the draw is revealed. `balls` may hold nulls for balls not yet shown.
 */
export function summarize(balls: readonly (number | null)[]): BoardSummary {
  const shown = balls.filter((b): b is number => b !== null)
  if (shown.length === 0) return EMPTY_SUMMARY
  const sum = shown.reduce((a, b) => a + b, 0)
  const hcnt = shown.filter((b) => b < 41).length
  const lcnt = shown.length - hcnt
  const dcnt = Math.floor((sum % 100) / 10)
  const tcnt = sum % 10
  const big = sum >= 811
  const odd = sum % 2 === 1
  const w: Partial<Record<OddCode, boolean>> = {
    o1: big, o2: !big, o3: odd, o4: !odd,
    o7: dcnt > tcnt, o8: dcnt === tcnt, o9: tcnt > dcnt,
    o10: hcnt > lcnt, o11: hcnt === lcnt, o12: lcnt > hcnt,
    o13: big && odd, o14: big && !odd, o15: !big && odd, o16: !big && !odd,
    o17: sum >= 210 && sum <= 695, o18: sum >= 696 && sum <= 763, o19: sum >= 764 && sum <= 855,
    o20: sum >= 856 && sum <= 923, o21: sum >= 924 && sum <= 1410,
  }
  return { sum, dcnt, tcnt, hcnt, lcnt, winners: w }
}

// ── Roadmap ────────────────────────────────────────────────────────────────────

export type TrendKind = 'bs' | 'oe' | 'dt' | 'hl' | 'fivee'
export const TREND_KINDS: TrendKind[] = ['bs', 'oe', 'dt', 'hl', 'fivee']

export interface DrawnRound {
  roundId: string
  roundNumber: number
  resultAt: string
  draw: KenoDraw
}

/** The odd code a draw lands on for one roadmap kind (the reference roadmap.php codes). */
export function trendCode(kind: TrendKind, d: KenoDraw): OddCode {
  switch (kind) {
    case 'bs': return ODD_OF[d.bigSmall]
    case 'oe': return ODD_OF[d.oddEven]
    case 'dt': return ODD_OF[d.dragonTiger]
    case 'hl': return ODD_OF[d.upDown]
    case 'fivee': return ODD_OF[d.element]
  }
}

export interface TrendCell { roundNumber: number; type: OddCode; round: DrawnRound }

/**
 * Port of the reference chunkArray(): oldest first, a run of the same outcome stacks down a
 * column up to six, a new outcome (or a seventh repeat) starts a new column, and only the
 * newest eighteen columns are kept. `rounds` arrive newest first, as the API returns them.
 */
export function trendColumns(kind: TrendKind, rounds: readonly DrawnRound[]): TrendCell[][] {
  const cells = rounds.map((r) => ({ roundNumber: r.roundNumber, type: trendCode(kind, r.draw), round: r }))
  const oldestFirst = cells.slice().reverse()
  const cols: TrendCell[][] = []
  let col: TrendCell[] = []
  let last: OddCode | null = null
  for (const c of oldestFirst) {
    if (c.type === last && col.length < 6) {
      col.push(c)
    } else {
      if (col.length) cols.push(col)
      col = [c]
      last = c.type
    }
  }
  if (col.length) cols.push(col)
  return cols.slice(-18)
}

/** The stat keys the dictionary uses for each roadmap kind, in display order. */
const STAT_KEYS: Record<TrendKind, { key: string; code: OddCode }[]> = {
  bs: [{ key: 'bs_b', code: 'o1' }, { key: 'bs_s', code: 'o2' }],
  oe: [{ key: 'oe_o', code: 'o3' }, { key: 'oe_e', code: 'o4' }],
  dt: [{ key: 'dt_d', code: 'o7' }, { key: 'dt_tie', code: 'o8' }, { key: 'dt_t', code: 'o9' }],
  hl: [{ key: 'hl_h', code: 'o10' }, { key: 'hl_t', code: 'o11' }, { key: 'hl_l', code: 'o12' }],
  fivee: [
    { key: 'fivee_go', code: 'o17' }, { key: 'fivee_wo', code: 'o18' }, { key: 'fivee_wa', code: 'o19' },
    { key: 'fivee_fi', code: 'o20' }, { key: 'fivee_ea', code: 'o21' },
  ],
}

/** Whole-number share of each outcome over the rounds given. */
export function trendStats(kind: TrendKind, rounds: readonly DrawnRound[]): { key: string; percent: number }[] {
  const n = rounds.length
  return STAT_KEYS[kind].map(({ key, code }) => ({
    key,
    percent: n === 0 ? 0 : Math.round((rounds.filter((r) => trendCode(kind, r.draw) === code).length / n) * 100),
  }))
}

/** Every bet a draw wins, as odd codes — the chips the roadmap popover lists. */
export function trendWinners(d: KenoDraw): OddCode[] {
  return TREND_KINDS.map((k) => trendCode(k, d))
}

// ── Time and references ────────────────────────────────────────────────────────

/**
 * Hours ahead of UTC that draw times are shown in. Thai time: the reference shows its own
 * server's GMT-4, which would put a Thai member's statement 11 hours off their clock. If a
 * deployment must show the reference's clock instead, this is the one place to change.
 */
export const DISPLAY_UTC_OFFSET_HOURS = 7

/** The short reference a member sees for a bet: its ticket number, or (older bets) the start of its id. */
export function ticketRef(ticketNo: string | null | undefined, betId: string): string {
  return ticketNo ? ticketNo : betId.slice(0, 8).toUpperCase()
}

// ── Numbers ────────────────────────────────────────────────────────────────────

/** Port of the reference display_numeric(): thousands commas, `decimals` places, blank for 0 unless `keepZero`. */
export function displayNumeric(v: number | string | null | undefined, decimals = 0, keepZero = false): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''))
  if (!Number.isFinite(n)) return ''
  const rounded = parseFloat(n.toFixed(2))
  if (!keepZero && rounded === 0) return ''
  const [int = '0', frac = ''] = rounded.toString().split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  if (decimals <= 0) return grouped
  return `${grouped}.${frac.slice(0, decimals).padEnd(decimals, '0')}`
}

/** "THB 1,234.50" style balance — the reference formatPrice(). */
export function formatPrice(v: number): string {
  return v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** Countdown seconds → "00:15" (or "01:05"), the reference displayCountDown. */
export function displayCountDown(seconds: number): string {
  if (!(seconds > 0)) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = seconds - 60 * m
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
